import React from "react";

import DOMPurify from 'dompurify';
import Cropper from "../Cropper/Cropper";
import { getCroppedImg } from "../Cropper/Cropper";
import FileExplorer from "../FileExplorer/FileExplorer";

const MODULE_SIZE_ROUND_TO = 50;
const DEFAULT_MODULE_SIZE = 200 - 2;
const NUM_ROW_CONTROLS = 2;//the number of divs in each row that aren't Modules (i.e. the delete button and grabber).
const MAX_NUM_MODULES = 10000; //Increase if we somehow ever need more than 10,000 modules.

//Simulates an abstract class
export class Module extends React.Component {
    static moduleMap = new Map();
    static deletedModuleIDs = [];
    static replacedIDs = [];
    static isDraggingModule = false;
    static canvas = document.createElement('canvas');
    static ctx = this.canvas.getContext('2d');

    moduleType = null;
    state = {
        id: null,
        row: null,
        column: null,
        width: DEFAULT_MODULE_SIZE,
        height: DEFAULT_MODULE_SIZE,
        content: {},

        canEdit: false,
        wasEdited: false,
    };

    constructor(props, defaultContent) {
        super();
        if (this.constructor === Module) {
            throw new Error("Class is of abstract type and can't be instantiated.");
        }
        if (this.render === undefined) {
            throw new Error("render method must be implemented in subclass.");
        };

        let id = props.id;

        //Failsafe - If more than one module somehow have the same id, give each a unique ID.
        if (Module.moduleMap.has("block" + id)) {//If a new duplicate is detected.
            //Delete all instances of that id from the database.
            Module.deletedModuleIDs.push(id);
            Module.replacedIDs.push(id);

            //Give the other module a new id (and update moduleMap);
            let newId = Module.generateNewIdNumber();
            Module.moduleMap.get("block" + id).setState({ id: "block" + newId, wasEdited: true });
            Module.moduleMap.set("block" + newId, Module.moduleMap.get("block" + id));
            Module.moduleMap.delete("block" + id);


            //Give this module a new id
            id = Module.generateNewIdNumber();

            //Set this to be saved as a new module
            this.state.wasEdited = true;
        }
        else if (Module.replacedIDs.includes(id)) {//If we detect another module with an ID that was already replaced. This one also has to be changed because all instanced of that ID will be deleted on the database.
            id = Module.generateNewIdNumber();
            this.state.wasEdited = true;
        }

        this.state.id = "block" + id;
        this.state.row = props.row;
        this.state.column = props.column;
        if (props.width) { this.state.width = props.width; }
        if (props.height) { this.state.height = props.height; }
        if (props.content) { this.state.content = props.content; }
        else { this.state.content = defaultContent; }
        if (props.canEdit) { this.state.canEdit = true; }
        Object.freeze(this.state);
        Module.moduleMap.set(this.state.id, this);
    }

    //generates unique ID for new Module to be saved on the database.
    static generateNewIdNumber() {
        let idTry = Math.floor(Math.random() * MAX_NUM_MODULES);
        while (this.moduleMap.has("block" + idTry) || this.replacedIDs.includes(idTry)) {
            Math.floor(Math.random() * MAX_NUM_MODULES);
        }
        return idTry;
    }

    //This method is called while the user is resizing a module (i.e. dragging the little tab). When it is let go, updateSize() is called.
    moduleResizeHandler() {
        if (!document.hasMouseUpEventListener) {
            document.hasMouseUpEventListener = true;
            document.addEventListener('mouseup', () => this.updateSize(), { once: true });
        }
    }

    //This method rounds the width and height to MODULE_SIZE_ROUND_TO and updates the member variables so the new size can be saved. 
    updateSize() {
        document.hasMouseUpEventListener = false;
        const element = document.getElementById(this.state.id);

        let roundedWidth = DEFAULT_MODULE_SIZE;
        let roundedHeight = DEFAULT_MODULE_SIZE;

        if (element.style.width) { //In case there is a bug and the width/height are not set.
            roundedWidth = Math.round(parseInt(element.style.width) / MODULE_SIZE_ROUND_TO) * MODULE_SIZE_ROUND_TO;
            roundedHeight = Math.round(parseInt(element.style.height) / MODULE_SIZE_ROUND_TO) * MODULE_SIZE_ROUND_TO;

            //min values:
            roundedWidth = Math.max(roundedWidth, MODULE_SIZE_ROUND_TO);
            roundedHeight = Math.max(roundedHeight, MODULE_SIZE_ROUND_TO);
        }

        roundedWidth -= 2;
        roundedHeight -= 2;

        element.style.width = roundedWidth + "px";
        element.style.height = roundedHeight + "px";
        this.setState({ wasEdited: true, width: roundedWidth, height: roundedHeight });

        if (this.updateCropperGrid) {
            this.updateCropperGrid(roundedWidth, roundedHeight);
        }

    }

    delete() {
        Module.deletedModuleIDs.push(this.state.id.substring(5)); //Removes the "block" from the HTML id. (i.e. "block1" -> "1")
        Module.moduleMap.delete(this.state.id);
        document.getElementById(this.state.id).remove();
        Module.renumberModules(this.state.row);
    }

    static renumberModules(row) {
        if (row == 0) { return; }

        const modules = Array.from(document.getElementById("row" + row).children)
            .filter((HTMLElement) => HTMLElement.tagName === 'DIV')
            .map((HTMLElement) => Module.moduleMap.get(HTMLElement.id));

        for (let c = 0; c < modules.length - NUM_ROW_CONTROLS; c++) {
            const module = modules[c];
            module.setState({ wasEdited: true, row: row, column: c + 1 });
        }
    }

    //Called by element being dragged.
    dragStart(event) {
        if (!/^block/.test(event.target.className)) {
            return;
        }

        Module.isDraggingModule = true;
        event.dataTransfer.setData("id", this.state.id);
        event.dataTransfer.setData("rowNum", this.state.row);
        event.dataTransfer.setData("colNum", this.state.column);
        event.target.style.margin = "5px";

        setTimeout(function () { //Hiding the element needs a small delay so HTML doesn't think you dropped it.
            event.target.style.visibility = "hidden";
        }, 1);
    }

    //Called by element dropped on.
    static drop(event) {
        event.preventDefault();

        const lineIndicator = document.getElementsByClassName("lineIndicator")[0];
        if (!lineIndicator) { return; }

        const id = event.dataTransfer.getData("id");
        const oldRow = event.dataTransfer.getData("rowNum");
        const oldColumn = event.dataTransfer.getData("colNum");
        const newRow = 1 * lineIndicator.getAttribute("row");
        const newColumn = 1 * lineIndicator.getAttribute("column");


        lineIndicator.after(document.getElementById(id));
        Module.moduleMap.get(id).setState({ row: newRow, column: newColumn });
        lineIndicator.remove();

        //Shifting the columns of elements in altered rows:
        Module.renumberModules(oldRow);
        Module.renumberModules(newRow);
    }

    //Called by element being dragged.
    static dragEnd(event) {
        const lineIndicator = document.getElementsByClassName("lineIndicator")[0];
        if (lineIndicator) { lineIndicator.remove(); }

        Module.isDraggingModule = false;
        event.target.style.margin = null;
        event.target.style.visibility = "visible";
    }

    //Called by element being dragged over.
    dragOver(event) {
        if (!(this.state.canEdit && Module.isDraggingModule)) {
            return;
        }

        event.preventDefault();
        const previousLineIndicator = document.getElementsByClassName("lineIndicator")[0];
        if (previousLineIndicator) { previousLineIndicator.remove(); }

        let module = event.target;
        while (!/^block/.test(module.className)) {
            module = module.parentElement;
        }

        let placementPosition, newColumn;
        if (event.clientX - module.getBoundingClientRect().left < module.clientWidth / 2) {//If on the left side of the module:
            placementPosition = "beforebegin";
            newColumn = this.state.column;
        }
        else {//If on the right side of the module:
            placementPosition = "afterend";
            newColumn = this.state.column + 1;
        }
        module.insertAdjacentHTML(placementPosition, "<div class=\"lineIndicator block\" style=\"height:" + module.clientHeight + "px\" row=\"" + this.state.row + "\" column=\"" + newColumn + "\"></div>");
    }

    //Called by element being dragged over.
    static dragLeave() {
        const lineIndicator = document.getElementsByClassName("lineIndicator")[0];
        if (lineIndicator) { lineIndicator.remove(); }
    }

    static bold(event) {
        event.preventDefault();
        if (window.getSelection().toString()) {
            document.execCommand('bold');
        }
    }

    static underline(event) {
        event.preventDefault();
        if (window.getSelection().toString()) {
            document.execCommand('underline');
        }
    }

    static italic(event) {
        event.preventDefault();
        if (window.getSelection().toString()) {
            document.execCommand('italic');
        }
    }

    static bullet(event) {
        event.preventDefault();
        try {
            const li = document.createElement('div');
            li.classList.add("bullet")
            window.getSelection().getRangeAt(0).surroundContents(li);
        }
        catch {
            console.error("No text field selected, but tried to add bullet!");
        }
    }

    static textAlign(event, align) {
        event.preventDefault();
        const targetModule = Module.moduleMap.get(event.target.parentElement.parentElement.parentElement.getAttribute("module"));
        if (targetModule) {
            targetModule.setState({ wasEdited: true, content: { ...targetModule.state.content, align: align } });
        }
    }

    static link(event) {
        event.preventDefault();
        if (window.getSelection().toString()) {
            let URL = prompt("Enter URL:");

            if (!URL) {
                return;
            }

            //Fixing link if necessary 
            if (!/^https?:\/\//i.test(URL)) {
                URL = 'http://' + URL;
            }

            //Creating link element
            let a = document.createElement('a');
            a.href = URL;
            a.target = "_blank";//Not working, appears to be a bug within JavaScript.
            a.title = a.href;
            window.getSelection().getRangeAt(0).surroundContents(a);
        }
    }

    render(moduleContent, styleModifier) {
        let style = { width: this.state.width + "px", height: this.state.height + "px" };
        if (styleModifier) {
            style = { ...styleModifier, ...style };
        }
        return (
            <div className="block" id={this.state.id} style={style} onMouseDown={(rect) => this.state.canEdit ? this.moduleResizeHandler() : {}} draggable={this.state.canEdit && window.matchMedia("only screen and (min-width: 768px)").matches} onDragStart={(event) => this.dragStart(event)} onDrop={(event) => Module.drop(event)} onDragEnd={(event) => Module.dragEnd(event)} onDragOver={(event) => this.dragOver(event)} onDragLeave={(event) => Module.dragLeave(event)}>
                <div className={"moduleDeleteButton"} onClick={() => this.delete()}>&#x2715;</div>
                {moduleContent}
                {/* The commented out lines below are for debugging */}
                {/* {this.state.id}<br></br> */}
                {/* {this.state.row} : {this.state.column} */}
                {/* {"\n" + JSON.stringify(this.state.content).replace("\'", "''")} */}
            </div>);
    }

}

export class SpacerModule extends Module {
    constructor(props) {
        if (!props.content) {
            super(props,
                //No content
                {}
            );
        }
        else {
            super(props);
        }
        this.moduleType = "SpacerModule";
    }

    render() {
        let style = { width: this.state.width + "px", height: this.state.height + "px" };
        return (
            <div className="block spacer" id={this.state.id} style={style} onMouseDown={(rect) => this.state.canEdit ? this.moduleResizeHandler() : {}} draggable={this.state.canEdit && window.matchMedia("only screen and (min-width: 768px)").matches} onDragStart={(event) => this.dragStart(event)} onDrop={(event) => Module.drop(event)} onDragEnd={(event) => Module.dragEnd(event)} onDragOver={(event) => this.dragOver(event)} onDragLeave={(event) => Module.dragLeave(event)}>
                <div className={"moduleDeleteButton"} onClick={() => this.delete()}>&#x2715;</div>
            </div>);
    }
}

export class Heading1Module extends Module {
    constructor(props) {
        if (!props.content) {
            super(props,
                //Default starter content
                { text: "Text", align: "center" }
            );
        }
        else {
            super(props);
        }
        this.moduleType = "Heading1Module";
    }

    //When the user selects the text field
    startTyping(event) {
        document.getElementById("textToolbar").classList.add("editing");
        document.getElementById("textToolbar").setAttribute("module", this.state.id);
    }

    //When th user deselects the text field
    endTyping(event) {
        document.getElementById("textToolbar").classList.remove("editing");
        document.getElementById("textToolbar").setAttribute("module", null);

        this.setState({ wasEdited: true, content: { ...this.state.content, text: event.target.innerHTML } });
    }

    render() {
        return super.render(<h1 className={"content"} style={{ textAlign: this.state.content.align }} onSelect={(event) => this.startTyping(event)} onBlur={(event) => this.endTyping(event)} contentEditable={this.state.canEdit} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(this.state.content.text) }} />);
    }
}

export class Heading2Module extends Module {
    constructor(props) {
        if (!props.content) {
            super(props,
                //Default starter content
                { text: "Text", align: "center" }
            );
        }
        else {
            super(props);
        }
        this.moduleType = "Heading2Module";
    }

    //When the user selects the text field
    startTyping(event) {
        document.getElementById("textToolbar").classList.add("editing");
        document.getElementById("textToolbar").setAttribute("module", this.state.id);
    }

    //When th user deselects the text field
    endTyping(event) {
        document.getElementById("textToolbar").classList.remove("editing");
        document.getElementById("textToolbar").setAttribute("module", null);

        this.setState({ wasEdited: true, content: { ...this.state.content, text: event.target.innerHTML } });
    }

    render() {
        return super.render(<h2 className={"content"} style={{ textAlign: this.state.content.align }} onSelect={(event) => this.startTyping(event)} onBlur={(event) => this.endTyping(event)} contentEditable={this.state.canEdit} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(this.state.content.text) }} />);
    }
}

export class Heading3Module extends Module {
    constructor(props) {
        if (!props.content) {
            super(props,
                //Default starter content
                { text: "Text", align: "center" }
            );
        }
        else {
            super(props);
        }
        this.moduleType = "Heading3Module";
    }

    //When the user selects the text field
    startTyping(event) {
        document.getElementById("textToolbar").classList.add("editing");
        document.getElementById("textToolbar").setAttribute("module", this.state.id);
    }

    //When th user deselects the text field
    endTyping(event) {
        document.getElementById("textToolbar").classList.remove("editing");
        document.getElementById("textToolbar").setAttribute("module", null);

        this.setState({ wasEdited: true, content: { ...this.state.content, text: event.target.innerHTML } });
    }

    render() {
        return super.render(<h3 className={"content"} style={{ textAlign: this.state.content.align }} onSelect={(event) => this.startTyping(event)} onBlur={(event) => this.endTyping(event)} contentEditable={this.state.canEdit} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(this.state.content.text) }} />);
    }
}

export class ParagraphModule extends Module {
    constructor(props) {
        if (!props.content) {
            super(props,
                //Default starter content
                { text: "Text", align: "center" }
            );
        }
        else {
            super(props);
        }
        this.moduleType = "ParagraphModule";
    }

    //When the user selects the text field
    startTyping(event) {
        document.getElementById("textToolbar").classList.add("editing");
        document.getElementById("textToolbar").setAttribute("module", this.state.id);
    }

    //When th user deselects the text field
    endTyping(event) {
        document.getElementById("textToolbar").classList.remove("editing");
        document.getElementById("textToolbar").setAttribute("module", null);

        this.setState({ wasEdited: true, content: { ...this.state.content, text: event.target.innerHTML } });
    }

    render() {
        return super.render(<p className={"content"} style={{ textAlign: this.state.content.align, marginTop: "2%", marginBottom: "2%", height: "96%" }} onSelect={(event) => this.startTyping(event)} onBlur={(event) => this.endTyping(event)} contentEditable={this.state.canEdit} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(this.state.content.text) }} />, { backgroundColor: "rgba(200, 200, 200, 0.2)", borderRadius: "10px" });
    }
}

export class MediaModule extends Module {
    constructor(props) {
        if (!props.content) {
            //Default starter content
            super(props, { "source": "icons/placeholder.jpg", "cropAreaPixels": { "width": 0, "height": 0, "x": 0, "y": 0 } });
        }
        else {
            super(props);
        }
        this.moduleType = "MediaModule";
        this.setState({ image: null, isImageEditMode: false, isExplorerMode: false });

        //If image, perform the crop.
        if (this.state.content.source.split('.').pop().toLowerCase() != "pdf" && this.state.content.source.split('.').pop().toLowerCase() != "mp4") {
            this.cropImage();
        }
    }

    beginImageEditMode() {
        this.setState({ isImageEditMode: true });
    }

    endImageEditMode(areaPixels) {
        this.setState({ wasEdited: true, isImageEditMode: false, content: { ...this.state.content, cropAreaPixels: areaPixels } }, () => this.cropImage());
        this.updateCropperGrid = null;
    }

    beginExplorerMode(event) {
        document.getElementById(this.state.id).draggable = false;
        this.setState({ isExplorerMode: true });
    }

    endExplorerMode(src) {
        if (src) {
            this.setState({ wasEdited: true, isExplorerMode: false, content: { ...this.state.content, source: src, cropAreaPixels: { width: 0, height: 0, x: 0, y: 0 } } }, () => {
                //if image, perform the crop.
                if (src.split('.').pop().toLowerCase() != "pdf" && src.split('.').pop().toLowerCase() != "mp4") {
                    this.cropImage();
                }
            });
        }
        else {
            this.setState({ isExplorerMode: false });
        }
        document.getElementById(this.state.id).draggable = true;
    }

    async cropImage() {
        try {
            const croppedImage = await getCroppedImg(this.state.content.source, this.state.content.cropAreaPixels, Module.canvas, Module.ctx);
            this.setState({ image: croppedImage });
        } catch (e) {
            console.error("Error loading image. url: " + this.state.content.source);
            this.setState({ image: "icons/placeholder.jpg" });
        }
    }

    doubleClickHandler() {//Only for images
        if (this.state.image == "icons/placeholder.jpg") {
            alert("You must chose a file before cropping.\nLeft click on this block to chose a file.");
            return;
        }
        this.beginImageEditMode();
    }

    rightClickHandler(event) {
        event.preventDefault();
        this.beginExplorerMode(event);
    }

    render() {//This block renders itself differently depending on what mode it's in.
        //In edit mode (images only), display the cropping tool
        if (this.state.isImageEditMode) {
            return super.render(<Cropper image={this.state.content.source} cropAreaPixels={this.state.content.cropAreaPixels} module={this} />, { transform: "translateZ(0)", backgroundColor: "transparent" });
        }
        //If explorer mode, show file explorer to select a file.
        else if (this.state.isExplorerMode) {
            return super.render(<FileExplorer password={Module.userPassword} dir={this.state.content.source === "icons/placeholder.jpg" ? "media" : this.state.content.source} module={this} />);
        }
        //If a PDF is selected
        else if (this.state.content.source.split('.').pop().toLowerCase() == "pdf") {
            //The proxy doesn`t really work with the iFrame. When running locally, add your local ip address to the start of the src!
            return super.render(<div class="iframeWrapper" style={{ width: "100%", height: "100%" }} onContextMenu={(event) => this.state.canEdit ? this.rightClickHandler(event) : {}} draggable={!this.state.canEdit}><iframe style={this.state.canEdit ? { pointerEvents: "none" } : {}} title={this.state.content.source} src={"https://docs.google.com/gview?url=ewb.wpi.edu/" + this.state.content.source + "&embedded=true"}></iframe></div>);
        }
        //If MP4 video is selected
        else if (this.state.content.source.split('.').pop().toLowerCase() == "mp4") {
            return super.render(<video style={{ width: "100%", height: "100%" }} src={this.state.content.source} onContextMenu={(event) => this.state.canEdit ? this.rightClickHandler(event) : {}} draggable={!this.state.canEdit} controls>Your browser does not support the video tag.</video>, { backgroundColor: "transparent" });
        }
        //If an image is selected
        else {
            return super.render(<img style={{ width: "100%", height: "100%", objectFit: "cover", overflow: "hidden" }} src={this.state.image} onDoubleClick={() => this.state.canEdit ? this.doubleClickHandler() : {}} onContextMenu={(event) => this.state.canEdit ? this.rightClickHandler(event) : {}} draggable={!this.state.canEdit} />, { backgroundColor: "transparent" });
        }
    }

}


export class FacebookModule extends Module {
    constructor(props) {
        if (!props.content) {
            super(props,
                //No content
                {}
            );
        }
        else {
            super(props);
        }
        this.moduleType = "FacebookModule";
    }

    render() {
        // return super.render(, { backgroundColor: "rgba(200, 200, 200, 0.2)", borderRadius: "10px" });
        if (this.state.canEdit) {
            return super.render(
                <div id={"facebookContainer"} style={{ width: "100%", height: "100%", maxWidth: "500px", backgroundColor: "lightgrey", margin: "auto", borderRadius: "10px", }}>
                    <img src="/icons/collage.png" width={"100%"} height={"100%"} style={{ objectFit: "contain" }} draggable={false} />
                </div>
            );
        }
        else {
            let width = this.state.width;
            //if in mobile view, find the correct width
            if (!window.matchMedia("only screen and (min-width: 768px)").matches) {
                width = Math.round(window.innerWidth * 0.95 + 0.5);
            }
            return super.render(
                <div id={"facebookContainer"} style={{ width: "100%", height: "100%", maxWidth: "500px", margin: "auto", backgroundColor: "#f5f6f7", borderRadius: "10px", overflow: "hidden" }}>
                    <iframe src={"https://www.facebook.com/plugins/page.php?href=https%3A%2F%2Fwww.facebook.com%2Fewbusawpi%2F&tabs=timeline&width=" + width + "&height=" + (this.state.height + 70) + "&small_header=true&adapt_container_width=true&hide_cover=true&show_facepile=false&appId"}
                        style={{ width: "100%", height: this.state.height + 70 + "px", position: "relative", top: "-70px", border: "none", overflow: "hidden" }}
                        frameborder="0" allowfullscreen="true" allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"></iframe>
                </div>
            );
        }
    }
}